import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Loader2, ArrowRight, Truck, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { formatPhoneNumber } from "@/lib/utils";

export interface CarrierData {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  mailingAddress?: string;
  docketPrefix?: string;
  docketNumber?: string;
  commonAuthorityStatus?: string;
  contractAuthorityStatus?: string;
  brokerAuthorityStatus?: string;
  commonApplicationPending?: boolean;
  contractApplicationPending?: boolean;
  brokerApplicationPending?: boolean;
  commonRevocationPending?: boolean;
  contractRevocationPending?: boolean;
  brokerRevocationPending?: boolean;
  propertyCarriage?: boolean;
  passengerCarriage?: boolean;
  householdGoodsCarriage?: boolean;
  privateCarriage?: boolean;
  bipdRequired?: string;
  bipdOnFile?: string;
  cargoRequired?: string;
  cargoOnFile?: string;
  bondRequired?: string;
  bondOnFile?: string;
}

interface FieldSelection {
  name: boolean;
  email: boolean;
  phone: boolean;
  address: boolean;
  mailingAddress: boolean;
  commonAuthorityStatus: boolean;
  contractAuthorityStatus: boolean;
  brokerAuthorityStatus: boolean;
  commonApplicationPending: boolean;
  contractApplicationPending: boolean;
  brokerApplicationPending: boolean;
  commonRevocationPending: boolean;
  contractRevocationPending: boolean;
  brokerRevocationPending: boolean;
  propertyCarriage: boolean;
  passengerCarriage: boolean;
  householdGoodsCarriage: boolean;
  privateCarriage: boolean;
  bipdRequired: boolean;
  bipdOnFile: boolean;
  cargoRequired: boolean;
  cargoOnFile: boolean;
  bondRequired: boolean;
  bondOnFile: boolean;
}

interface CarrierForModal {
  id: number;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  mailingAddress?: string | null;
  docketPrefix?: string | null;
  docketNumber?: string | null;
  commonAuthorityStatus?: string | null;
  contractAuthorityStatus?: string | null;
  brokerAuthorityStatus?: string | null;
  commonApplicationPending?: boolean | null;
  contractApplicationPending?: boolean | null;
  brokerApplicationPending?: boolean | null;
  commonRevocationPending?: boolean | null;
  contractRevocationPending?: boolean | null;
  brokerRevocationPending?: boolean | null;
  propertyCarriage?: boolean | null;
  passengerCarriage?: boolean | null;
  householdGoodsCarriage?: boolean | null;
  privateCarriage?: boolean | null;
  bipdRequired?: string | null;
  bipdOnFile?: string | null;
  cargoRequired?: string | null;
  cargoOnFile?: string | null;
  bondRequired?: string | null;
  bondOnFile?: string | null;
}

interface FMCSACarrierRefreshModalProps {
  open: boolean;
  onClose: () => void;
  carrier: CarrierForModal;
  onApply: (data: Partial<CarrierData>) => void;
  isUpdating?: boolean;
}

export default function FMCSACarrierRefreshModal({
  open,
  onClose,
  carrier,
  onApply,
  isUpdating = false
}: FMCSACarrierRefreshModalProps) {
  const { toast } = useToast();
  const [fmcsaData, setFmcsaData] = useState<CarrierData | null>(null);
  const [fieldSelection, setFieldSelection] = useState<FieldSelection>({
    name: true,
    email: true,
    phone: true,
    address: true,
    mailingAddress: true,
    commonAuthorityStatus: true,
    contractAuthorityStatus: true,
    brokerAuthorityStatus: true,
    commonApplicationPending: true,
    contractApplicationPending: true,
    brokerApplicationPending: true,
    commonRevocationPending: true,
    contractRevocationPending: true,
    brokerRevocationPending: true,
    propertyCarriage: true,
    passengerCarriage: true,
    householdGoodsCarriage: true,
    privateCarriage: true,
    bipdRequired: true,
    bipdOnFile: true,
    cargoRequired: true,
    cargoOnFile: true,
    bondRequired: true,
    bondOnFile: true,
  });

  const fetchMutation = useMutation({
    mutationFn: async () => {
      if (!carrier.docketPrefix || !carrier.docketNumber) {
        throw new Error("Docket number is required to fetch FMCSA data");
      }

      const response = await apiRequest("POST", "/api/fmcsa/fetch-by-docket", {
        docketPrefix: carrier.docketPrefix,
        docketNumber: carrier.docketNumber,
      });

      const data = await response.json();
      return data as CarrierData;
    },
    onSuccess: (data) => {
      setFmcsaData(data);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to fetch FMCSA data",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (open && carrier.docketPrefix && carrier.docketNumber && !fmcsaData && !fetchMutation.isPending) {
      fetchMutation.mutate();
    }
  }, [open, carrier.docketPrefix, carrier.docketNumber]);

  const handleApply = () => {
    if (!fmcsaData) return;

    const updateData: Partial<CarrierData> = {};

    Object.keys(fieldSelection).forEach((key) => {
      const fieldKey = key as keyof FieldSelection;
      if (fieldSelection[fieldKey] && fmcsaData[fieldKey] !== undefined) {
        (updateData as any)[fieldKey] = fmcsaData[fieldKey];
      }
    });

    onApply(updateData);
  };

  const toggleField = (field: keyof FieldSelection) => {
    setFieldSelection(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const selectAll = () => {
    const allSelected: FieldSelection = {} as FieldSelection;
    Object.keys(fieldSelection).forEach(key => {
      allSelected[key as keyof FieldSelection] = true;
    });
    setFieldSelection(allSelected);
  };

  const deselectAll = () => {
    const noneSelected: FieldSelection = {} as FieldSelection;
    Object.keys(fieldSelection).forEach(key => {
      noneSelected[key as keyof FieldSelection] = false;
    });
    setFieldSelection(noneSelected);
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return "(not set)";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const selectedCount = Object.values(fieldSelection).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Refresh FMCSA Data - {carrier.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {!carrier.docketNumber ? (
            <div className="flex items-center gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <p className="text-sm text-yellow-800">
                This carrier doesn't have a docket number. Please add a docket number first to fetch FMCSA data.
              </p>
            </div>
          ) : fetchMutation.isPending ? (
            <div className="text-center py-12">
              <Loader2 className="w-10 h-10 mx-auto mb-4 text-primary animate-spin" />
              <p className="text-gray-600 font-medium">Fetching latest FMCSA data...</p>
              <p className="text-sm text-gray-500 mt-1">
                {carrier.docketPrefix}-{carrier.docketNumber}
              </p>
            </div>
          ) : fetchMutation.isError ? (
            <div className="text-center py-8">
              <AlertCircle className="w-10 h-10 mx-auto mb-4 text-red-500" />
              <p className="text-gray-600 font-medium">Failed to fetch FMCSA data</p>
              <p className="text-sm text-red-500 mt-1">{fetchMutation.error?.message || "Unknown error"}</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => fetchMutation.mutate()}
              >
                Try Again
              </Button>
            </div>
          ) : !fmcsaData ? (
            <div className="text-center py-12">
              <Loader2 className="w-10 h-10 mx-auto mb-4 text-primary animate-spin" />
              <p className="text-gray-600">Initializing...</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Select which fields to update. Uncheck fields you want to keep unchanged.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
                  <Button variant="outline" size="sm" onClick={deselectAll}>Deselect All</Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Basic Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <FieldRow
                      label="Carrier Name"
                      fieldKey="name"
                      currentValue={carrier.name}
                      fmcsaValue={fmcsaData.name}
                      selected={fieldSelection.name}
                      onChange={() => toggleField('name')}
                    />
                    <FieldRow
                      label="Email"
                      fieldKey="email"
                      currentValue={carrier.email}
                      fmcsaValue={fmcsaData.email}
                      selected={fieldSelection.email}
                      onChange={() => toggleField('email')}
                    />
                    <FieldRow
                      label="Phone"
                      fieldKey="phone"
                      currentValue={formatPhoneNumber(carrier.phone)}
                      fmcsaValue={formatPhoneNumber(fmcsaData.phone)}
                      selected={fieldSelection.phone}
                      onChange={() => toggleField('phone')}
                    />
                    <FieldRow
                      label="Address"
                      fieldKey="address"
                      currentValue={carrier.address}
                      fmcsaValue={fmcsaData.address}
                      selected={fieldSelection.address}
                      onChange={() => toggleField('address')}
                    />
                    <FieldRow
                      label="Mailing Address"
                      fieldKey="mailingAddress"
                      currentValue={carrier.mailingAddress}
                      fmcsaValue={fmcsaData.mailingAddress}
                      selected={fieldSelection.mailingAddress}
                      onChange={() => toggleField('mailingAddress')}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Authority Status</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <FieldRow
                      label="Common Authority"
                      fieldKey="commonAuthorityStatus"
                      currentValue={carrier.commonAuthorityStatus}
                      fmcsaValue={fmcsaData.commonAuthorityStatus}
                      selected={fieldSelection.commonAuthorityStatus}
                      onChange={() => toggleField('commonAuthorityStatus')}
                    />
                    <FieldRow
                      label="Contract Authority"
                      fieldKey="contractAuthorityStatus"
                      currentValue={carrier.contractAuthorityStatus}
                      fmcsaValue={fmcsaData.contractAuthorityStatus}
                      selected={fieldSelection.contractAuthorityStatus}
                      onChange={() => toggleField('contractAuthorityStatus')}
                    />
                    <FieldRow
                      label="Broker Authority"
                      fieldKey="brokerAuthorityStatus"
                      currentValue={carrier.brokerAuthorityStatus}
                      fmcsaValue={fmcsaData.brokerAuthorityStatus}
                      selected={fieldSelection.brokerAuthorityStatus}
                      onChange={() => toggleField('brokerAuthorityStatus')}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Pending Applications</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <FieldRow
                      label="Common App Pending"
                      fieldKey="commonApplicationPending"
                      currentValue={carrier.commonApplicationPending}
                      fmcsaValue={fmcsaData.commonApplicationPending}
                      selected={fieldSelection.commonApplicationPending}
                      onChange={() => toggleField('commonApplicationPending')}
                      isBoolean
                    />
                    <FieldRow
                      label="Contract App Pending"
                      fieldKey="contractApplicationPending"
                      currentValue={carrier.contractApplicationPending}
                      fmcsaValue={fmcsaData.contractApplicationPending}
                      selected={fieldSelection.contractApplicationPending}
                      onChange={() => toggleField('contractApplicationPending')}
                      isBoolean
                    />
                    <FieldRow
                      label="Broker App Pending"
                      fieldKey="brokerApplicationPending"
                      currentValue={carrier.brokerApplicationPending}
                      fmcsaValue={fmcsaData.brokerApplicationPending}
                      selected={fieldSelection.brokerApplicationPending}
                      onChange={() => toggleField('brokerApplicationPending')}
                      isBoolean
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Revocation Status</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <FieldRow
                      label="Common Revocation"
                      fieldKey="commonRevocationPending"
                      currentValue={carrier.commonRevocationPending}
                      fmcsaValue={fmcsaData.commonRevocationPending}
                      selected={fieldSelection.commonRevocationPending}
                      onChange={() => toggleField('commonRevocationPending')}
                      isBoolean
                    />
                    <FieldRow
                      label="Contract Revocation"
                      fieldKey="contractRevocationPending"
                      currentValue={carrier.contractRevocationPending}
                      fmcsaValue={fmcsaData.contractRevocationPending}
                      selected={fieldSelection.contractRevocationPending}
                      onChange={() => toggleField('contractRevocationPending')}
                      isBoolean
                    />
                    <FieldRow
                      label="Broker Revocation"
                      fieldKey="brokerRevocationPending"
                      currentValue={carrier.brokerRevocationPending}
                      fmcsaValue={fmcsaData.brokerRevocationPending}
                      selected={fieldSelection.brokerRevocationPending}
                      onChange={() => toggleField('brokerRevocationPending')}
                      isBoolean
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Operating Authority</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <FieldRow
                      label="Property Carriage"
                      fieldKey="propertyCarriage"
                      currentValue={carrier.propertyCarriage}
                      fmcsaValue={fmcsaData.propertyCarriage}
                      selected={fieldSelection.propertyCarriage}
                      onChange={() => toggleField('propertyCarriage')}
                      isBoolean
                    />
                    <FieldRow
                      label="Passenger Carriage"
                      fieldKey="passengerCarriage"
                      currentValue={carrier.passengerCarriage}
                      fmcsaValue={fmcsaData.passengerCarriage}
                      selected={fieldSelection.passengerCarriage}
                      onChange={() => toggleField('passengerCarriage')}
                      isBoolean
                    />
                    <FieldRow
                      label="Household Goods"
                      fieldKey="householdGoodsCarriage"
                      currentValue={carrier.householdGoodsCarriage}
                      fmcsaValue={fmcsaData.householdGoodsCarriage}
                      selected={fieldSelection.householdGoodsCarriage}
                      onChange={() => toggleField('householdGoodsCarriage')}
                      isBoolean
                    />
                    <FieldRow
                      label="Private Carriage"
                      fieldKey="privateCarriage"
                      currentValue={carrier.privateCarriage}
                      fmcsaValue={fmcsaData.privateCarriage}
                      selected={fieldSelection.privateCarriage}
                      onChange={() => toggleField('privateCarriage')}
                      isBoolean
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Insurance Status</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <FieldRow
                      label="BIPD Required"
                      fieldKey="bipdRequired"
                      currentValue={carrier.bipdRequired}
                      fmcsaValue={fmcsaData.bipdRequired}
                      selected={fieldSelection.bipdRequired}
                      onChange={() => toggleField('bipdRequired')}
                    />
                    <FieldRow
                      label="BIPD On File"
                      fieldKey="bipdOnFile"
                      currentValue={carrier.bipdOnFile}
                      fmcsaValue={fmcsaData.bipdOnFile}
                      selected={fieldSelection.bipdOnFile}
                      onChange={() => toggleField('bipdOnFile')}
                    />
                    <FieldRow
                      label="Cargo Required"
                      fieldKey="cargoRequired"
                      currentValue={carrier.cargoRequired}
                      fmcsaValue={fmcsaData.cargoRequired}
                      selected={fieldSelection.cargoRequired}
                      onChange={() => toggleField('cargoRequired')}
                    />
                    <FieldRow
                      label="Cargo On File"
                      fieldKey="cargoOnFile"
                      currentValue={carrier.cargoOnFile}
                      fmcsaValue={fmcsaData.cargoOnFile}
                      selected={fieldSelection.cargoOnFile}
                      onChange={() => toggleField('cargoOnFile')}
                    />
                  </CardContent>
                </Card>
              </div>

              <Separator />

              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {selectedCount} field(s) selected for update
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose} disabled={isUpdating}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApply}
                    disabled={selectedCount === 0 || isUpdating}
                    data-testid="button-apply-fmcsa-refresh"
                  >
                    {isUpdating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Apply Selected Updates"
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({
  label,
  fieldKey,
  currentValue,
  fmcsaValue,
  selected,
  onChange,
  isBoolean = false
}: {
  label: string;
  fieldKey: string;
  currentValue: any;
  fmcsaValue: any;
  selected: boolean;
  onChange: () => void;
  isBoolean?: boolean;
}) {
  const formatVal = (val: any) => {
    if (val === null || val === undefined) return "(not set)";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    return String(val);
  };

  const currentDisplay = formatVal(currentValue);
  const fmcsaDisplay = formatVal(fmcsaValue);
  const isDifferent = currentDisplay !== fmcsaDisplay;
  const hasValue = fmcsaValue !== null && fmcsaValue !== undefined;

  return (
    <label className={`flex items-start gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${selected && hasValue ? 'bg-green-50' : ''} ${isDifferent ? 'border-l-2 border-yellow-400' : ''}`}>
      <Checkbox
        checked={selected && hasValue}
        onCheckedChange={onChange}
        disabled={!hasValue}
        data-testid={`checkbox-field-${fieldKey}`}
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{label}</div>
        {isDifferent ? (
          <div className="flex items-center gap-2 text-xs mt-1">
            <span className="text-gray-500 line-through truncate max-w-[100px]">
              {currentDisplay}
            </span>
            <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <span className="text-green-700 font-medium truncate max-w-[100px]">
              {fmcsaDisplay}
            </span>
          </div>
        ) : (
          <div className="text-xs text-gray-500 mt-1">
            {currentDisplay} (no change)
          </div>
        )}
      </div>
    </label>
  );
}
